import { useMemo, useRef, useState } from "react";
import api from "../lib/api";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import { FileSearch, Loader2, Upload, Check, AlertTriangle, Sparkles, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { ProrogaLetterDialog, LETTER_LABELS } from "./ProrogaLetterDialog";

// extracted key → employee field mapping
const FIELD_MAP = [
    { src: "lavoratore_cognome", field: "cognome", label: "Cognome" },
    { src: "lavoratore_nome", field: "nome", label: "Nome" },
    { src: "lavoratore_codice_fiscale", field: "codice_fiscale", label: "Codice fiscale" },
    { src: "lavoratore_data_nascita", field: "data_nascita", label: "Data di nascita", type: "date" },
    { src: "lavoratore_comune_nascita", field: "luogo_nascita", label: "Luogo di nascita" },
    { src: "lavoratore_indirizzo", field: "indirizzo", label: "Indirizzo" },
    { src: "lavoratore_comune_domicilio", field: "citta", label: "Città" },
    { src: "lavoratore_cap", field: "cap", label: "CAP" },
    { src: "data_inizio", field: "data_assunzione", label: "Data assunzione", type: "date" },
    { src: "data_cessazione", field: "data_cessazione", label: "Data cessazione", type: "date" },
    { src: "data_fine", field: "contratto_scadenza", label: "Scadenza contratto", type: "date" },
    { src: "tipo_contratto", field: "contratto_tipo", label: "Tipo contratto" },
    { src: "ore_settimanali", field: "contratto_ore", label: "Ore settimanali", type: "number" },
    { src: "qualifica", field: "qualifica", label: "Qualifica" },
];

const fmtVal = (v, type) => {
    if (v === null || v === undefined || v === "") return "—";
    if (type === "date") {
        try {
            const [y, m, d] = String(v).split("-");
            if (y && m && d) return `${d}/${m}/${y}`;
        } catch { /* raw */ }
    }
    return String(v);
};

const norm = (v) => String(v ?? "").trim().toLowerCase();

export const UnilavDialog = ({ open, onOpenChange, employee, onUpdated }) => {
    const [file, setFile] = useState(null);
    const [extracting, setExtracting] = useState(false);
    const [extracted, setExtracted] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [applying, setApplying] = useState(false);
    const [archivePdf, setArchivePdf] = useState(true);
    const [markReminder, setMarkReminder] = useState(true);
    const [prorogaOffer, setProrogaOffer] = useState(null); // { recordId } after archiving a proroga
    const [prorogaOpen, setProrogaOpen] = useState(false);
    const inputRef = useRef(null);

    const reset = () => {
        setFile(null);
        setExtracted(null);
        setSelected(new Set());
        setProrogaOffer(null);
    };

    const handleOpenChange = (o) => {
        if (!o) reset();
        onOpenChange(o);
    };

    const handleExtract = async () => {
        if (!file) return;
        setExtracting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/unilav/extract", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 120000,
            });
            setExtracted(data.extracted || {});
            // Pre-select rows where the employee record is empty and UNILAV has a value
            const pre = new Set();
            FIELD_MAP.forEach((f) => {
                const v = (data.extracted || {})[f.src];
                const cur = employee?.[f.field];
                if (v !== null && v !== undefined && v !== "" && (cur === null || cur === undefined || cur === "")) {
                    pre.add(f.field);
                }
            });
            setSelected(pre);
            toast.success("Documento analizzato");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore nell'analisi AI del documento");
        } finally {
            setExtracting(false);
        }
    };

    const handleApply = async () => {
        if (selected.size === 0 && !archivePdf && !markReminder) return;
        setApplying(true);
        const results = [];
        try {
            // 1. Update the employee record with the selected fields
            if (selected.size > 0) {
                const payload = {};
                FIELD_MAP.forEach((f) => {
                    if (selected.has(f.field)) {
                        let v = extracted[f.src];
                        if (f.type === "number" && v !== null && v !== undefined) v = Number(v);
                        payload[f.field] = v;
                    }
                });
                await api.put(`/employees/${employee.id}`, payload);
                results.push(`${selected.size} campi aggiornati`);
            }

            // 2. Archive the PDF in the employee's file (object storage — may be out of budget)
            if (archivePdf && file) {
                try {
                    const fd = new FormData();
                    fd.append("file", file);
                    fd.append("employee_id", employee.id);
                    fd.append("category", "unilav");
                    const tipo = extracted.tipo_comunicazione ? ` ${extracted.tipo_comunicazione}` : "";
                    fd.append("title", `Ricevuta UNILAV${tipo}${extracted.data_invio ? ` del ${fmtVal(extracted.data_invio, "date")}` : ""}`);
                    await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    results.push("PDF archiviato nel fascicolo");
                } catch (err) {
                    const code = err.response?.status;
                    if (code === 402 || code === 503) {
                        toast.warning("PDF non archiviato: budget storage esaurito (Profilo → Universal Key → Add Balance)");
                    } else {
                        toast.warning("PDF non archiviato: errore nel caricamento");
                    }
                }
            }

            // 3. Mark the matching UNILAV reminder as sent
            if (markReminder && extracted.tipo_comunicazione) {
                try {
                    const { data: reminders } = await api.get("/unilav/reminders", { params: { status: "pending" } });
                    const match = reminders.find(
                        (r) => r.employee_id === employee.id && r.event_type === extracted.tipo_comunicazione
                    );
                    if (match) {
                        await api.put(`/unilav/reminders/${match.id}`, { status: "done" });
                        results.push("promemoria UNILAV segnato inviato");
                    }
                } catch { /* non-blocking */ }
            }

            // 4. Save the extracted data (prefills the contract generation form)
            let recordId = null;
            try {
                const { data: rec } = await api.post("/unilav/records", { employee_id: employee.id, extracted, source: "dialog" });
                recordId = rec?.id || null;
                if (rec?.employee_update) {
                    const eu = rec.employee_update;
                    const ore = eu.contratto_ore ? ` · ore settimanali aggiornate a ${String(eu.contratto_ore).replace(".", ",")}h` : "";
                    toast.info((eu.contratto_scadenza === null
                        ? "Scheda aggiornata: contratto trasformato a INDETERMINATO (scadenza rimossa)"
                        : `Scheda aggiornata: contratto prorogato al ${(eu.contratto_scadenza || "").split("-").reverse().join("/")}`) + ore);
                }
            } catch { /* non-blocking */ }

            toast.success(results.length ? results.join(" · ") : "Fatto");
            onUpdated && onUpdated();
            const tipoKind = (extracted.tipo_comunicazione || "").toLowerCase();
            if (LETTER_LABELS[tipoKind]) {
                // Keep the dialog open: offer the legal notice to the employee
                setProrogaOffer({ recordId, kind: tipoKind });
                return;
            }
            handleOpenChange(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore nell'aggiornamento della scheda");
        } finally {
            setApplying(false);
        }
    };

    const rows = useMemo(() => (
        extracted
            ? FIELD_MAP.map((f) => {
                  const uni = extracted[f.src];
                  const cur = employee?.[f.field];
                  const hasUni = uni !== null && uni !== undefined && uni !== "";
                  const hasCur = cur !== null && cur !== undefined && cur !== "";
                  let status = "empty"; // no UNILAV value
                  if (hasUni && hasCur) status = norm(uni) === norm(cur) ? "match" : "diff";
                  else if (hasUni && !hasCur) status = "new";
                  return { ...f, uni, cur, status, hasUni };
              })
            : []
    ), [extracted, employee]);

    const visibleRows = useMemo(() => rows.filter((r) => r.hasUni || r.cur), [rows]);

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="unilav-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2 text-[#376DB3]">
                        <FileSearch className="h-5 w-5" />
                        Verifica UNILAV (AI)
                    </DialogTitle>
                    <DialogDescription>
                        Carica la ricevuta UNILAV in PDF: l'AI estrae i dati e li confronta con la scheda di{" "}
                        {employee?.cognome} {employee?.nome}.
                    </DialogDescription>
                </DialogHeader>

                {!extracted && (
                    <div className="space-y-3">
                        <input
                            type="file"
                            accept="application/pdf"
                            ref={inputRef}
                            className="hidden"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            data-testid="unilav-file-input"
                        />
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            className="flex w-full flex-col items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-sm text-slate-500 transition hover:border-[#376DB3]/50 hover:bg-[#376DB3]/5"
                            data-testid="unilav-dropzone"
                        >
                            <Upload className="h-6 w-6 text-slate-400" />
                            {file ? (
                                <span className="font-medium text-slate-700">{file.name}</span>
                            ) : (
                                <span>Clicca per selezionare la ricevuta UNILAV (PDF)</span>
                            )}
                        </button>
                        <Button
                            onClick={handleExtract}
                            disabled={!file || extracting}
                            className="w-full gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                            data-testid="unilav-extract-btn"
                        >
                            {extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                            {extracting ? "Analisi in corso… (10-30 sec)" : "Analizza con AI"}
                        </Button>
                    </div>
                )}

                {prorogaOffer && (
                    <div className="rounded-lg border border-[#376DB3]/30 bg-[#376DB3]/5 p-4" data-testid="unilav-proroga-offer">
                        <div className="flex items-start gap-3">
                            <FileSignature className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#376DB3]" />
                            <div className="text-sm text-slate-700">
                                <p className="font-semibold text-slate-800">UNILAV di {prorogaOffer.kind} archiviato.</p>
                                <p className="mt-1 text-xs text-slate-600">
                                    Vuoi generare ora la comunicazione di {prorogaOffer.kind} da consegnare al dipendente ({LETTER_LABELS[prorogaOffer.kind].law})? Potrai controllare le date{prorogaOffer.kind === "proroga" ? ", il numero proroga e l'eventuale causale" : " e aggiungere eventuali note"} prima di inviarla al portale per la firma.
                                </p>
                                <Button
                                    onClick={() => setProrogaOpen(true)}
                                    className="mt-3 gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                                    data-testid="unilav-proroga-letter-btn"
                                >
                                    <FileSignature className="h-4 w-4" /> {LETTER_LABELS[prorogaOffer.kind].button}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {extracted && !prorogaOffer && (
                    <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="unilav-summary">
                            {extracted.tipo_comunicazione && (
                                <Badge className="bg-[#376DB3] text-white capitalize">{extracted.tipo_comunicazione}</Badge>
                            )}
                            {extracted.codice_comunicazione && <span>Cod. {extracted.codice_comunicazione}</span>}
                            {extracted.data_invio && <span>· inviata il {fmtVal(extracted.data_invio, "date")}</span>}
                            {extracted.ccnl && <span>· CCNL {extracted.ccnl}</span>}
                        </div>

                        <div className="overflow-x-auto rounded-md border border-slate-200">
                            <table className="w-full min-w-[520px] text-xs">
                                <thead className="bg-slate-50 text-left uppercase tracking-wide text-slate-500">
                                    <tr>
                                        <th className="px-2 py-2 w-8"></th>
                                        <th className="px-2 py-2">Campo</th>
                                        <th className="px-2 py-2">UNILAV</th>
                                        <th className="px-2 py-2">Scheda</th>
                                        <th className="px-2 py-2 w-16">Esito</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleRows.map((r) => (
                                        <tr key={r.field} className="border-t border-slate-100" data-testid={`unilav-row-${r.field}`}>
                                            <td className="px-2 py-1.5">
                                                {r.hasUni && r.status !== "match" && (
                                                    <Checkbox
                                                        checked={selected.has(r.field)}
                                                        onCheckedChange={(c) => {
                                                            setSelected((prev) => {
                                                                const s = new Set(prev);
                                                                if (c) s.add(r.field); else s.delete(r.field);
                                                                return s;
                                                            });
                                                        }}
                                                        data-testid={`unilav-check-${r.field}`}
                                                    />
                                                )}
                                            </td>
                                            <td className="px-2 py-1.5 font-medium text-slate-700">{r.label}</td>
                                            <td className="px-2 py-1.5">{fmtVal(r.uni, r.type)}</td>
                                            <td className="px-2 py-1.5 text-slate-500">{fmtVal(r.cur, r.type)}</td>
                                            <td className="px-2 py-1.5">
                                                {r.status === "match" && <span className="inline-flex items-center gap-1 text-emerald-600"><Check className="h-3 w-3" /> OK</span>}
                                                {r.status === "diff" && <span className="inline-flex items-center gap-1 text-amber-600"><AlertTriangle className="h-3 w-3" /> Diverso</span>}
                                                {r.status === "new" && <span className="text-[#376DB3]">Nuovo</span>}
                                                {r.status === "empty" && <span className="text-slate-300">—</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="text-[11px] text-slate-500">
                            Seleziona i campi da riportare nella scheda dipendente (pre-selezionati quelli mancanti). Verifica sempre i dati estratti dall'AI.
                        </p>

                        <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5">
                            <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-700">
                                <Checkbox
                                    checked={archivePdf}
                                    onCheckedChange={(c) => setArchivePdf(!!c)}
                                    data-testid="unilav-archive-check"
                                />
                                Archivia il PDF nel fascicolo del dipendente (categoria "Ricevuta UNILAV")
                            </label>
                            <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-700">
                                <Checkbox
                                    checked={markReminder}
                                    onCheckedChange={(c) => setMarkReminder(!!c)}
                                    data-testid="unilav-mark-reminder-check"
                                />
                                Segna il promemoria UNILAV corrispondente come inviato
                            </label>
                        </div>
                    </div>
                )}

                <DialogFooter>
                    {extracted && (
                        <Button variant="outline" onClick={reset} data-testid="unilav-restart">
                            Analizza un altro PDF
                        </Button>
                    )}
                    <Button variant="outline" onClick={() => handleOpenChange(false)} data-testid="unilav-close">
                        Chiudi
                    </Button>
                    {extracted && !prorogaOffer && (
                        <Button
                            onClick={handleApply}
                            disabled={applying || (selected.size === 0 && !archivePdf && !markReminder)}
                            className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                            data-testid="unilav-apply-btn"
                        >
                            {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                            Applica {selected.size > 0 ? `(${selected.size})` : ""}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
            <ProrogaLetterDialog
                open={prorogaOpen}
                onOpenChange={setProrogaOpen}
                employeeId={employee?.id}
                recordId={prorogaOffer?.recordId}
                kind={prorogaOffer?.kind || "proroga"}
                onDone={() => {
                    onUpdated && onUpdated();
                    handleOpenChange(false);
                }}
            />
        </Dialog>
    );
};
